import { useCallback, useEffect, useRef, useState } from 'react';
import server from 'utils/server';

export const SCRIBE_MODEL_ID = 'scribe_v2_realtime';
const SCRIBE_BASE_URI = 'wss://api.elevenlabs.io/v1/speech-to-text/realtime';
const SCRIBE_SAMPLE_RATE = 16000;
const SCRIBE_CHUNK_SIZE = 4096;

type ScribeTokenResponse =
  | string
  | {
      token?: string;
      access_token?: string;
      value?: string;
      secret?: string;
    };

const getScribeTokenValue = (data: ScribeTokenResponse) => {
  if (typeof data === 'string') return data;

  return data.token ?? data.access_token ?? data.value ?? data.secret ?? '';
};

export const loadScribeToken = async () => {
  const { data } = (await server.get('/v03/other/scribe-token')) as {
    data: ScribeTokenResponse;
  };
  const token = getScribeTokenValue(data);

  if (!token) {
    throw new Error('Scribe-Token konnte nicht geladen werden.');
  }

  return token;
};

export const joinTranscriptParts = (...parts: string[]) =>
  parts
    .map((part) => part.trim())
    .filter(Boolean)
    .join(' ');

export type ScribeStatus =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'transcribing'
  | 'error';

export type RealtimeScribeOptions = {
  modelId: string;
  languageCode?: string;
  vadSilenceThresholdSecs?: number;
  keyterms?: string[];
  noVerbatim?: boolean;
  onPartialTranscript?: (message: { text: string }) => void;
  onCommittedTranscript?: (message: { text: string }) => void;
  onError?: (error: Error) => void;
};

const encodePcm16ToBase64 = (pcm: Int16Array) => {
  const bytes = new Uint8Array(pcm.buffer);
  let binary = '';

  for (let index = 0; index < bytes.length; index += 1) {
    binary += String.fromCharCode(bytes[index]);
  }

  return window.btoa(binary);
};

const floatSamplesToPcm16 = (samples: number[]) => {
  const pcm = new Int16Array(samples.length);

  samples.forEach((sample, index) => {
    const clampedSample = Math.max(-1, Math.min(1, sample));
    pcm[index] =
      clampedSample < 0 ? clampedSample * 0x8000 : clampedSample * 0x7fff;
  });

  return pcm;
};

const downsampleAudio = (
  input: Float32Array,
  inputSampleRate: number,
  outputSampleRate: number,
) => {
  if (inputSampleRate === outputSampleRate) {
    return input;
  }

  const ratio = inputSampleRate / outputSampleRate;
  const outputLength = Math.round(input.length / ratio);
  const output = new Float32Array(outputLength);
  let inputOffset = 0;

  for (let outputOffset = 0; outputOffset < outputLength; outputOffset += 1) {
    const nextInputOffset = Math.round((outputOffset + 1) * ratio);
    let accumulator = 0;
    let count = 0;

    for (
      let index = inputOffset;
      index < nextInputOffset && index < input.length;
      index += 1
    ) {
      accumulator += input[index];
      count += 1;
    }

    output[outputOffset] = count ? accumulator / count : 0;
    inputOffset = nextInputOffset;
  }

  return output;
};

const getAudioContext = () => {
  const AudioContextConstructor =
    window.AudioContext || (window as any).webkitAudioContext;

  if (!AudioContextConstructor) {
    throw new Error('Spracheingabe wird in diesem Browser nicht unterstützt.');
  }

  return new AudioContextConstructor() as AudioContext;
};

const getScribeErrorMessage = (message: any) => {
  if (typeof message?.error === 'string') return message.error;
  if (typeof message?.message === 'string') return message.message;

  return 'Spracheingabe konnte nicht verarbeitet werden.';
};

const useRealtimeScribe = ({
  modelId,
  languageCode,
  vadSilenceThresholdSecs,
  keyterms,
  noVerbatim,
  onPartialTranscript,
  onCommittedTranscript,
  onError,
}: RealtimeScribeOptions) => {
  const websocketRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const pendingSamplesRef = useRef<number[]>([]);
  const closedByUserRef = useRef(false);
  const onPartialTranscriptRef = useRef(onPartialTranscript);
  const onCommittedTranscriptRef = useRef(onCommittedTranscript);
  const onErrorRef = useRef(onError);
  const [status, setStatus] = useState<ScribeStatus>('disconnected');
  const [partialTranscript, setPartialTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    onPartialTranscriptRef.current = onPartialTranscript;
  }, [onPartialTranscript]);

  useEffect(() => {
    onCommittedTranscriptRef.current = onCommittedTranscript;
  }, [onCommittedTranscript]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const handleError = useCallback((nextError: Error) => {
    setError(nextError.message);
    setStatus('error');
    onErrorRef.current?.(nextError);
  }, []);

  const buildWebsocketUrl = useCallback(
    (token: string) => {
      const params = new URLSearchParams({
        model_id: modelId,
        token,
        commit_strategy: 'vad',
      });

      if (languageCode) params.append('language_code', languageCode);
      if (vadSilenceThresholdSecs) {
        params.append(
          'vad_silence_threshold_secs',
          String(vadSilenceThresholdSecs),
        );
      }
      if (noVerbatim !== undefined) {
        params.append('no_verbatim', noVerbatim ? 'true' : 'false');
      }
      keyterms?.forEach((term) => params.append('keyterms', term));

      return `${SCRIBE_BASE_URI}?${params.toString()}`;
    },
    [keyterms, languageCode, modelId, noVerbatim, vadSilenceThresholdSecs],
  );

  const sendAudioChunk = useCallback((samples: number[], commit = false) => {
    const websocket = websocketRef.current;

    if (!websocket || websocket.readyState !== WebSocket.OPEN) {
      return;
    }

    websocket.send(
      JSON.stringify({
        message_type: 'input_audio_chunk',
        audio_base_64: samples.length
          ? encodePcm16ToBase64(floatSamplesToPcm16(samples))
          : '',
        commit,
        sample_rate: SCRIBE_SAMPLE_RATE,
      }),
    );
  }, []);

  const flushPendingAudio = useCallback(
    (commit = false) => {
      const pendingSamples = pendingSamplesRef.current.splice(0);

      if (pendingSamples.length || commit) {
        sendAudioChunk(pendingSamples, commit);
      }
    },
    [sendAudioChunk],
  );

  const cleanupAudio = useCallback(() => {
    if (processorRef.current) {
      processorRef.current.onaudioprocess = null;
      processorRef.current.disconnect();
      processorRef.current = null;
    }

    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }

    if (gainRef.current) {
      gainRef.current.disconnect();
      gainRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(() => undefined);
    }

    audioContextRef.current = null;
    pendingSamplesRef.current = [];
  }, []);

  const startMicrophoneStreaming = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error(
        'Mikrofonzugriff wird in diesem Browser nicht unterstützt.',
      );
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
      },
    });
    const audioContext = getAudioContext();
    const source = audioContext.createMediaStreamSource(stream);
    const processor = audioContext.createScriptProcessor(SCRIBE_CHUNK_SIZE, 1, 1);
    const gain = audioContext.createGain();

    gain.gain.value = 0;

    processor.onaudioprocess = (event) => {
      const inputData = event.inputBuffer.getChannelData(0);
      const outputData = downsampleAudio(
        inputData,
        audioContext.sampleRate,
        SCRIBE_SAMPLE_RATE,
      );

      for (let index = 0; index < outputData.length; index += 1) {
        pendingSamplesRef.current.push(outputData[index]);
      }

      while (pendingSamplesRef.current.length >= SCRIBE_CHUNK_SIZE) {
        const nextChunk = pendingSamplesRef.current.splice(
          0,
          SCRIBE_CHUNK_SIZE,
        );
        sendAudioChunk(nextChunk);
      }
    };

    source.connect(processor);
    processor.connect(gain);
    gain.connect(audioContext.destination);

    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }

    streamRef.current = stream;
    audioContextRef.current = audioContext;
    sourceRef.current = source;
    processorRef.current = processor;
    gainRef.current = gain;
  }, [sendAudioChunk]);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      let message: any;

      try {
        message = JSON.parse(event.data);
      } catch (parseError) {
        handleError(new Error('Scribe-Antwort konnte nicht gelesen werden.'));
        return;
      }

      switch (message.message_type) {
        case 'session_started':
          setStatus((currentStatus) =>
            currentStatus === 'transcribing' ? currentStatus : 'connected',
          );
          break;
        case 'partial_transcript': {
          const text = message.text ?? '';
          setPartialTranscript(text);
          setStatus('transcribing');
          onPartialTranscriptRef.current?.({ text });
          break;
        }
        case 'committed_transcript':
        case 'committed_transcript_with_timestamps': {
          const text = message.text ?? '';
          setPartialTranscript('');
          onCommittedTranscriptRef.current?.({ text });
          break;
        }
        case 'auth_error':
        case 'quota_exceeded':
        case 'commit_throttled':
        case 'transcriber_error':
        case 'unaccepted_terms':
        case 'rate_limited':
        case 'input_error':
        case 'queue_overflow':
        case 'resource_exhausted':
        case 'session_time_limit_exceeded':
        case 'chunk_size_exceeded':
        case 'insufficient_audio_activity':
        case 'error':
          handleError(new Error(getScribeErrorMessage(message)));
          break;
        default:
          break;
      }
    },
    [handleError],
  );

  const disconnect = useCallback(
    (updateState = true) => {
      closedByUserRef.current = true;
      flushPendingAudio(true);
      cleanupAudio();

      if (websocketRef.current) {
        websocketRef.current.close(1000, 'User ended session');
        websocketRef.current = null;
      }

      if (updateState) {
        setPartialTranscript('');
        setStatus('disconnected');
      }
    },
    [cleanupAudio, flushPendingAudio],
  );

  const connect = useCallback(
    ({ token }: { token: string }) =>
      new Promise<void>((resolve, reject) => {
        // Only an existing OPEN socket counts as already connected; a stale
        // CONNECTING/CLOSING/CLOSED ref must not resolve as if connected.
        if (websocketRef.current) {
          if (websocketRef.current.readyState === WebSocket.OPEN) {
            resolve();
          } else {
            reject(new Error('Spracheingabe ist noch nicht bereit.'));
          }
          return;
        }

        let settled = false;
        const rejectConnection = (nextError: Error) => {
          if (!settled) {
            settled = true;
            reject(nextError);
          }
          handleError(nextError);
        };

        closedByUserRef.current = false;
        setStatus('connecting');
        setError(null);
        setPartialTranscript('');

        const websocket = new WebSocket(buildWebsocketUrl(token));
        websocketRef.current = websocket;

        websocket.onopen = async () => {
          if (closedByUserRef.current) {
            return;
          }

          try {
            await startMicrophoneStreaming();
            setStatus('transcribing');

            if (!settled) {
              settled = true;
              resolve();
            }
          } catch (nextError: any) {
            cleanupAudio();
            websocket.close();
            websocketRef.current = null;
            rejectConnection(
              nextError instanceof Error
                ? nextError
                : new Error('Spracheingabe konnte nicht gestartet werden.'),
            );
          }
        };

        websocket.onmessage = handleMessage;

        websocket.onerror = () => {
          rejectConnection(
            new Error(
              'Verbindung zur Spracheingabe konnte nicht aufgebaut werden.',
            ),
          );
        };

        websocket.onclose = () => {
          cleanupAudio();
          websocketRef.current = null;

          // Settle a still-pending connect() so an awaiting caller doesn't hang
          // if the socket closes before onopen/onerror fire.
          if (!settled) {
            settled = true;
            if (closedByUserRef.current) {
              resolve();
            } else {
              reject(
                new Error('Verbindung zur Spracheingabe wurde geschlossen.'),
              );
            }
          }

          if (!closedByUserRef.current) {
            setStatus((currentStatus) =>
              currentStatus === 'error' ? currentStatus : 'disconnected',
            );
          }
        };
      }),
    [
      buildWebsocketUrl,
      cleanupAudio,
      handleError,
      handleMessage,
      startMicrophoneStreaming,
    ],
  );

  const commit = useCallback(() => {
    flushPendingAudio(true);
  }, [flushPendingAudio]);

  const clearTranscripts = useCallback(() => {
    setPartialTranscript('');
    pendingSamplesRef.current = [];
  }, []);

  useEffect(() => () => disconnect(false), [disconnect]);

  return {
    status,
    isConnected: status === 'connected' || status === 'transcribing',
    isTranscribing: status === 'transcribing',
    partialTranscript,
    error,
    connect,
    disconnect,
    commit,
    clearTranscripts,
  };
};

export default useRealtimeScribe;
